import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogIn, UserPlus, Loader2, MailCheck, CheckCircle2, KeyRound, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortalLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetSent, setResetSent] = useState(false);

  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Register state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: ''
  });

  // Handle verification token or reset token from URL
  useEffect(() => {
    const verifyToken = searchParams.get('verify');
    if (verifyToken) {
      verifyEmail(verifyToken);
    }
    const resetParam = searchParams.get('reset');
    if (resetParam) {
      setResetToken(resetParam);
      setShowResetPassword(true);
    }
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      await axios.post(`${API}/auth/verify-email?token=${token}`);
      setVerificationSuccess(true);
      toast.success('E-Mail erfolgreich verifiziert! Sie koennen sich jetzt anmelden.');
    } catch (error) {
      toast.error('Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast.success('Erfolgreich angemeldet!');
      // Check if user is admin and redirect accordingly
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data.is_admin) {
          navigate('/admin');
        } else {
          navigate('/portal');
        }
      } catch {
        navigate('/portal');
      }
    } else {
      toast.error(result.error || 'Anmeldung fehlgeschlagen');
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    // Validate name
    if (registerData.name.trim().length < 2) {
      toast.error('Name muss mindestens 2 Zeichen lang sein');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (registerData.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    // Check password strength
    const hasUpperCase = /[A-Z]/.test(registerData.password);
    const hasLowerCase = /[a-z]/.test(registerData.password);
    const hasNumbers = /\d/.test(registerData.password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      toast.error('Passwort muss Groß- und Kleinbuchstaben sowie Zahlen enthalten');
      return;
    }

    setLoading(true);

    const result = await register(
      registerData.email,
      registerData.password,
      registerData.name,
      registerData.phone
    );
    
    if (result.success) {
      if (result.verification_required) {
        setVerificationSent(true);
        toast.success('Registrierung erfolgreich! Bitte pruefen Sie Ihre E-Mail zur Verifizierung.');
      } else {
        toast.success('Erfolgreich registriert! Willkommen bei Kathodik.');
        navigate('/portal');
      }
    } else {
      toast.error(result.error || 'Registrierung fehlgeschlagen');
    }
    
    setLoading(false);
  };

  return (
    <div className="bg-white min-h-screen py-20 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8 hero-reveal">
            <img
              src="https://customer-assets.emergentagent.com/job_metal-coating/artifacts/4mzqxaj5_A80F545A-F543-476F-BF3A-7169BDADA022.png"
              alt="Kathodik"
              className="h-20 w-auto mx-auto mb-4"
            />
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#2c7a7b] mb-2">Kundenportal</p>
            <h1 className="text-3xl font-bold text-slate-800 mb-2" data-testid="portal-login-heading">Anmelden</h1>
            <p className="text-slate-500">Verwalten Sie Ihre Aufträge und Anfragen</p>
          </div>

          {verificationSuccess && (
            <Card className="bg-green-50 border-green-300 mb-6">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-green-800">E-Mail verifiziert!</h3>
                <p className="text-green-700 text-sm mt-2">Sie koennen sich jetzt anmelden.</p>
              </CardContent>
            </Card>
          )}

          {verificationSent && (
            <Card className="bg-blue-50 border-blue-300 mb-6">
              <CardContent className="p-6 text-center">
                <MailCheck className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-blue-800">Verifizierungs-E-Mail gesendet!</h3>
                <p className="text-blue-700 text-sm mt-2">
                  Bitte pruefen Sie Ihre E-Mail und klicken Sie auf den Verifizierungslink.
                </p>
                <Button
                  variant="outline"
                  className="mt-4 border-blue-300 text-blue-700"
                  onClick={() => { setVerificationSent(false); navigate('/portal'); }}
                >
                  Weiter zum Portal
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white border-slate-300" data-testid="portal-login-card">
            <CardContent className="p-6">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login">Anmelden</TabsTrigger>
                  <TabsTrigger value="register">Registrieren</TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" className="text-slate-800 mb-2 block">
                        E-Mail *
                      </Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        placeholder="ihre@email.de"
                        className="bg-white border-slate-300"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <Label htmlFor="login-password" className="text-slate-800 mb-2 block">
                        Passwort *
                      </Label>
                      <Input
                        id="login-password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="••••••••"
                        className="bg-white border-slate-300"
                        required
                        disabled={loading}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#2c7a7b] hover:bg-[#285e61] text-white py-6 text-lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Anmelden...
                        </>
                      ) : (
                        <>
                          <LogIn className="h-5 w-5 mr-2" />
                          Anmelden
                        </>
                      )}
                    </Button>
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-[#2c7a7b] hover:underline cursor-pointer"
                        data-testid="forgot-password-link"
                      >
                        Passwort vergessen?
                      </button>
                    </div>
                  </form>
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="register-name" className="text-slate-800 mb-2 block">
                        Name *
                      </Label>
                      <Input
                        id="register-name"
                        type="text"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        placeholder="Max Mustermann"
                        className="bg-white border-slate-300"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <Label htmlFor="register-email" className="text-slate-800 mb-2 block">
                        E-Mail *
                      </Label>
                      <Input
                        id="register-email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        placeholder="ihre@email.de"
                        className="bg-white border-slate-300"
                        required
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <Label htmlFor="register-phone" className="text-slate-800 mb-2 block">
                        Telefon (optional)
                      </Label>
                      <Input
                        id="register-phone"
                        type="tel"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        placeholder="0123 456789"
                        className="bg-white border-slate-300"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <Label htmlFor="register-password" className="text-slate-800 mb-2 block">
                        Passwort * (min. 8 Zeichen)
                      </Label>
                      <Input
                        id="register-password"
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        placeholder="••••••••"
                        className="bg-white border-slate-300"
                        required
                        minLength={8}
                        disabled={loading}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Muss Groß- und Kleinbuchstaben sowie Zahlen enthalten
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="register-confirm-password" className="text-slate-800 mb-2 block">
                        Passwort bestätigen *
                      </Label>
                      <Input
                        id="register-confirm-password"
                        type="password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        placeholder="••••••••"
                        className="bg-white border-slate-300"
                        required
                        disabled={loading}
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#2c7a7b] hover:bg-[#285e61] text-white py-6 text-lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Registrieren...
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-5 w-5 mr-2" />
                          Registrieren
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="text-center mt-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-[#2c7a7b]"
            >
              ← Zurück zur Startseite
            </Button>
          </div>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="forgot-password-modal">
              <Card className="w-full max-w-md bg-white">
                <CardContent className="p-6">
                  {resetSent ? (
                    <div className="text-center">
                      <MailCheck className="h-12 w-12 text-[#2c7a7b] mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-slate-800 mb-2">E-Mail gesendet!</h3>
                      <p className="text-slate-600 text-sm mb-4">
                        Falls ein Konto mit dieser Adresse existiert, wurde ein Link zum Zurücksetzen gesendet.
                      </p>
                      <Button onClick={() => { setShowForgotPassword(false); setResetSent(false); }} className="w-full bg-[#2c7a7b] hover:bg-[#285e61] text-white">
                        Schließen
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center space-x-3 mb-6">
                        <KeyRound className="h-6 w-6 text-[#2c7a7b]" />
                        <h3 className="text-xl font-bold text-slate-800">Passwort vergessen?</h3>
                      </div>
                      <p className="text-slate-600 text-sm mb-4">
                        Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
                      </p>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        setLoading(true);
                        try {
                          await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
                          setResetSent(true);
                          toast.success('Reset-E-Mail gesendet!');
                        } catch (error) {
                          toast.error('Fehler beim Senden der E-Mail');
                        }
                        setLoading(false);
                      }}>
                        <Label className="text-slate-800 mb-2 block font-semibold">E-Mail-Adresse</Label>
                        <Input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="ihre@email.de"
                          className="bg-white border-slate-300 mb-4"
                          required
                          data-testid="forgot-email-input"
                        />
                        <div className="flex gap-3">
                          <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForgotPassword(false)}>
                            Abbrechen
                          </Button>
                          <Button type="submit" className="flex-1 bg-[#2c7a7b] hover:bg-[#285e61] text-white" disabled={loading} data-testid="forgot-submit-btn">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Link senden'}
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Reset Password Modal */}
          {showResetPassword && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reset-password-modal">
              <Card className="w-full max-w-md bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <KeyRound className="h-6 w-6 text-[#2c7a7b]" />
                    <h3 className="text-xl font-bold text-slate-800">Neues Passwort setzen</h3>
                  </div>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (newPassword !== confirmNewPassword) {
                      toast.error('Passwörter stimmen nicht überein');
                      return;
                    }
                    if (newPassword.length < 6) {
                      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
                      return;
                    }
                    setLoading(true);
                    try {
                      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
                      toast.success('Passwort erfolgreich geändert!');
                      setShowResetPassword(false);
                      setNewPassword('');
                      setConfirmNewPassword('');
                      navigate('/portal/login');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
                    }
                    setLoading(false);
                  }}>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-800 mb-2 block font-semibold">Neues Passwort</Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Mindestens 6 Zeichen"
                          className="bg-white border-slate-300"
                          required
                          data-testid="new-password-input"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-800 mb-2 block font-semibold">Passwort bestätigen</Label>
                        <Input
                          type="password"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          placeholder="Passwort wiederholen"
                          className="bg-white border-slate-300"
                          required
                          data-testid="confirm-new-password-input"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full mt-6 bg-[#2c7a7b] hover:bg-[#285e61] text-white py-6" disabled={loading} data-testid="reset-submit-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Passwort ändern'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
